import React, { useState } from "react";
import { Plus, X, Tag } from "lucide-react";
import { useBuilder, BlockConfig, LineItem } from "../BuilderContext";
import PremiumModal from "../../../ui/PremiumModal";
import { feeItemsApi } from "../../../../services/api";

const fmt = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const FeesTableBlock: React.FC<{ block: BlockConfig }> = ({ block }) => {
  const { state, dispatch } = useBuilder();
  const heading = block.settings.headingOverride || "Fees";
  const [showCatalog, setShowCatalog] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickForm, setQuickForm] = useState({ code: "", name: "", category: "legal_fee", amountCents: 0, taxable: true });
  const [quickSaving, setQuickSaving] = useState(false);

  const fees = state.lineItems.filter((li) => li.kind === "fee");
  const feeIndices = state.lineItems.map((li, i) => li.kind === "fee" ? i : -1).filter((i) => i >= 0);

  const handleAddManual = () => {
    dispatch({
      type: "ADD_LINE_ITEM",
      item: {
        kind: "fee",
        description: "",
        quantity: 1,
        unitPriceCents: 0,
        amountCents: 0,
        taxable: true,
        taxAmountCents: 0,
      },
    });
  };

  const handleAddFromCatalog = (feeItem: any) => {
    dispatch({
      type: "ADD_LINE_ITEM",
      item: {
        kind: "fee",
        feeItemId: feeItem._id,
        description: feeItem.name,
        quantity: feeItem.defaultQuantity || 1,
        unitPriceCents: feeItem.defaultPrice?.amountCents || 0,
        amountCents: (feeItem.defaultQuantity || 1) * (feeItem.defaultPrice?.amountCents || 0),
        taxable: feeItem.tax?.taxable ?? true,
        taxAmountCents: 0,
      },
    });
    setShowCatalog(false);
  };

  const handleUpdate = (feeIdx: number, field: string, value: any) => {
    const realIdx = feeIndices[feeIdx];
    const item = state.lineItems[realIdx];
    const updated: Partial<LineItem> = { [field]: value };
    if (field === "quantity" || field === "unitPriceCents") {
      const qty = field === "quantity" ? value : item.quantity;
      const upc = field === "unitPriceCents" ? value : item.unitPriceCents;
      updated.amountCents = Math.round(qty * upc);
    }
    if (field === "amountCents") {
      const qty = item.quantity || 1;
      updated.unitPriceCents = Math.round(value / qty);
    }
    dispatch({ type: "UPDATE_LINE_ITEM", index: realIdx, item: updated });
  };

  const handleRemove = (feeIdx: number) => {
    dispatch({ type: "REMOVE_LINE_ITEM", index: feeIndices[feeIdx] });
  };

  const handleQuickCreateFee = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuickSaving(true);
    try {
      const res = await feeItemsApi.create({
        code: quickForm.code,
        name: quickForm.name,
        category: quickForm.category,
        defaultPrice: { amountCents: quickForm.amountCents, currency: "CAD" },
        tax: { taxable: quickForm.taxable },
      });
      handleAddFromCatalog(res.data);
      setShowQuickCreate(false);
      setQuickForm({ code: "", name: "", category: "legal_fee", amountCents: 0, taxable: true });
    } catch (err) {
      console.error("Failed to create fee item:", err);
    } finally {
      setQuickSaving(false);
    }
  };

  return (
    <div className="py-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{heading}</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2" style={{ borderColor: "var(--color-primary)" }}>
            <th className="text-left py-2 pr-2 text-xs font-semibold text-slate-600">Description</th>
            <th className="text-right py-2 px-2 text-xs font-semibold text-slate-600 w-20">Qty</th>
            <th className="text-right py-2 px-2 text-xs font-semibold text-slate-600 w-28">Unit Price</th>
            <th className="text-right py-2 pl-2 text-xs font-semibold text-slate-600 w-28">Amount</th>
            <th className="w-8 print:hidden" />
          </tr>
        </thead>
        <tbody>
          {fees.map((li, feeIdx) => (
            <tr key={feeIdx} className="border-b border-slate-100 transition-colors hover:bg-slate-50/60 group">
              <td className="py-2 pr-2">
                <input
                  type="text"
                  value={li.description}
                  onChange={(e) => handleUpdate(feeIdx, "description", e.target.value)}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                  placeholder="Description"
                />
              </td>
              <td className="py-2 px-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={li.quantity}
                  onChange={(e) => handleUpdate(feeIdx, "quantity", parseFloat(e.target.value) || 0)}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-right text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                />
              </td>
              <td className="py-2 px-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={li.unitPriceCents ? (li.unitPriceCents / 100).toFixed(2) : ""}
                  onChange={(e) => handleUpdate(feeIdx, "unitPriceCents", Math.round(parseFloat(e.target.value || "0") * 100))}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-right text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                />
              </td>
              <td className="py-2 pl-2">
                <div className="relative">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none select-none">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={li.amountCents ? (li.amountCents / 100).toFixed(2) : ""}
                    onChange={(e) => handleUpdate(feeIdx, "amountCents", Math.round(parseFloat(e.target.value || "0") * 100))}
                    className="w-full bg-white/80 border border-slate-200 text-sm text-right font-medium text-slate-800 rounded-lg pl-5 pr-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                    placeholder="0.00"
                  />
                </div>
              </td>
              <td className="py-2 text-center print:hidden">
                <button onClick={() => handleRemove(feeIdx)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
          {fees.length === 0 && (
            <tr>
              <td colSpan={5} className="py-6 text-center text-sm text-slate-400">
                No fee items yet
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex gap-2 mt-3 print:hidden">
        <button
          onClick={handleAddManual}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
        >
          <Plus className="h-3.5 w-3.5" />
          Manual Entry
        </button>
        <button
          onClick={() => setShowCatalog(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
        >
          <Tag className="h-3.5 w-3.5" />
          From Fee Catalog
        </button>
      </div>

      {/* Fee Catalog Picker Modal */}
      <PremiumModal
        open={showCatalog}
        onClose={() => setShowCatalog(false)}
        title="Fee Item Catalog"
        icon={Tag}
        size="lg"
      >
        <div className="space-y-2">
          {state.feeItems.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No fee items in catalog</p>
          ) : (
            state.feeItems.map((fi: any) => (
              <button
                key={fi._id}
                onClick={() => handleAddFromCatalog(fi)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-left"
              >
                <div>
                  <p className="text-sm font-medium text-slate-800">{fi.name}</p>
                  <p className="text-xs text-slate-500">{fi.code}  -  {fi.category}</p>
                </div>
                <span className="text-sm font-semibold text-slate-700">{fmt(fi.defaultPrice?.amountCents || 0)}</span>
              </button>
            ))
          )}
          <button
            onClick={() => { setShowCatalog(false); setShowQuickCreate(true); }}
            className="w-full flex items-center gap-2 p-3 rounded-lg border border-dashed border-slate-300 text-sm font-medium hover:bg-slate-50"
            style={{ color: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Create New Fee Item
          </button>
        </div>
      </PremiumModal>

      {/* Quick Create Fee Item Modal */}
      <PremiumModal
        open={showQuickCreate}
        onClose={() => setShowQuickCreate(false)}
        title="New Fee Item"
        icon={Tag}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowQuickCreate(false)}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="quickCreateFeeForm"
              disabled={quickSaving || !quickForm.code || !quickForm.name}
              className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {quickSaving ? "Creating..." : "Create & Add"}
            </button>
          </div>
        }
      >
        <form id="quickCreateFeeForm" onSubmit={handleQuickCreateFee} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Code *</label>
              <input
                type="text"
                required
                value={quickForm.code}
                onChange={(e) => setQuickForm({ ...quickForm, code: e.target.value.toUpperCase() })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="FEE_001"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Price ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={quickForm.amountCents ? (quickForm.amountCents / 100).toFixed(2) : ""}
                onChange={(e) => setQuickForm({ ...quickForm, amountCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              required
              value={quickForm.name}
              onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Consultation Fee"
            />
          </div>
        </form>
      </PremiumModal>
    </div>
  );
};

export default FeesTableBlock;
